import { HttpClient } from "@/App/services/httpClient/HttpClient";
import type { LoginRequestBody } from "@/Pages/Login/types/LoginRequestBody";
import storageService, { StorageKey } from "@/App/services/storage.service";
import type { AuthorizationResponse } from "@/Pages/Login/types/AuthorizationResponse";
import UserService from "@/Pages/Login/services/user.service";
import router from "@/App/router/router";
import type { ForgotPasswordRequestBody } from "../types/ForgotRequest";
import config from "@/App/config/main";
import { getAuthorizationHeader } from "@/App/services/httpClient/HttpEngine";
import type { User } from "@/App/user/User";
import type { LocationQuery } from "vue-router";
import { RoutePath } from "@/App/router/routePath";
import { urlValidator } from "@/App/utils/urlValidator";

const loginClientInformation = {
  twiageClient: "dashboard2",
  platform: "web",
} as const;

export default {
  async login({ username, password }: { username: string; password: string }): Promise<User> {
    const loginRequestBody: LoginRequestBody = {
      username,
      password,
      ...loginClientInformation,
    };
    const response = await HttpClient.post<AuthorizationResponse>({
      path: "/auth/signin",
      body: loginRequestBody,
    }).then((response) => {
      storageService.removeItem(StorageKey.loginViaSso);
      return response;
    });
    return this.onTokenRetrieved(response.token);
  },

  isUserPreferToLoginViaSso(): boolean {
    return !!storageService.getItem(StorageKey.loginViaSso);
  },

  setupLoginViaSso({ signoutUrl }: { signoutUrl: string | undefined }): void {
    storageService.save(StorageKey.loginViaSso, true, false);
    if (urlValidator(signoutUrl)) {
      storageService.save(StorageKey.ssoSignOutUrl, signoutUrl, false);
    } else {
      storageService.removeItem(StorageKey.ssoSignOutUrl);
    }
  },

  onTokenRetrieved(token: string): Promise<User> {
    storageService.save(StorageKey.jwt, token, false);
    return UserService.retrieveUserData();
  },

  isLoggedIn(): boolean {
    return this.getAuthToken() != null;
  },

  getAuthToken(): string | null {
    return storageService.getItem(StorageKey.jwt);
  },

  getSsoSignoutUrl(): string | null {
    return storageService.getItem(StorageKey.ssoSignOutUrl);
  },

  async requestToken(request: ForgotPasswordRequestBody): Promise<void> {
    const returnURL = router.currentRoute.value.query["return-url"];
    const forgotPasswordRequestBody = {
      returnURL,
      username: request.username,
    };
    await HttpClient.post({
      path: `${config.API_URL}/v3/auth/forgot`,
      body: forgotPasswordRequestBody,
    });
  },

  logout({ reason }: { reason?: string }): void {
    const token = this.getAuthToken();
    if (token) {
      HttpClient.post({ path: "/auth/signout", headers: { ...getAuthorizationHeader({ token }) } });
      storageService.removeItem(StorageKey.jwt);
    }
    UserService.clearUser();
    UserService.stopAllUserActivity();
    if (reason) {
      storageService.save(StorageKey.previousSessionExpiredMessage, reason, false);
    }
  },

  createSsoUrl({ query }: { query: LocationQuery }): string {
    const url = new URL(config.SSO_URL);
    const redirectUrl = new URL(window.location.origin + RoutePath.authHandleToken);
    if (query.redirect) {
      redirectUrl.searchParams.append("redirect", query.redirect.toString());
    }
    url.searchParams.append("redirectUri", redirectUrl.toString());
    url.searchParams.append("twiageClient", "dashboard");
    url.searchParams.append("platform", "web");
    if (query.ssoProvider) {
      url.searchParams.append("ssoProvider", query.ssoProvider.toString());
    }
    return url.toString();
  },
};
